
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import {
  filterDays,
  applyManualNotAFit,
  createAdminSourceCorrectionIdempotencyKey,
  getHygienistStats
} from "@/api";
import type { HygienistStats } from "@/api";
import { constants } from "@/utils/constants";
import {
  getFfsStatusColor,
  getFfsStatusLabel,
  ffsStatusNeedsDarkText,
  resolveCanonicalFfsPresentationCode
} from "@/helpers/ffsStatus";
import { startCase } from "lodash";
import { getNewMemberReviewReasonCategory } from "@/helpers/membershipReasonCategory";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import ShiftsInDialog from "./components/ShiftsInDialog";
import SignupReviewDialog from "@/components/SignupReviewDialog.vue";

export default Vue.extend({
  name: "MemberReview",
  components: {
    ShiftsInDialog,
    SignupReviewDialog
  },
  data: () => {
    return {
      tableSearch: "",
      showModal: false,
      modalHeader: "",
      // T2-6429: Hold/Block both route to the FFS "Not a Fit" flow (T2-6396),
      // which requires a support note AND a ticket reference. modalAction tracks
      // which button was clicked so the audit note records Hold vs Block.
      modalAction: "" as "hold" | "block" | "",
      modalNote: "",
      modalTicketRef: "",
      modalSubmitting: false,
      modalIdempotencyKey: "",
      modalData: {
        id: 0,
        firstName: "",
        email: "",
        status: 0
      },
      hygienistTypeCode: constants.hygienistType,
      // Stats dialog
      showStatsDialog: false,
      loadingStats: false,
      selectedItemId: null as number | null,
      talentStats: null as HygienistStats | null
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent",
      countDays: "countDays"
    }),
    tableHeaders(): Array<headersInterface> {
      const tableColumns = constants.enabledColumns.memberReview;
      const data = tableColumns.map(i => {
        return { align: "left", text: startCase(i), value: i };
      });
      return [
        ...data,
        { align: "left", text: "User shifts", value: "userShifts" },
        { align: "left", text: "Actions", value: "actions" }
      ];
    },
    filteredContent(): any[] {
      const items = (this as any).content || [];
      const search = ((this as any).tableSearch || "").trim().toLowerCase();
      
      if (!search) {
        return items;
      }

      // Only search these relevant columns
      const searchableColumns = [
        "id",
        "firstName",
        "lastName",
        "email",
        "city",
        "province",
        "phone",
        "CDHONumber"
      ];

      return items.filter((item: any) => {
        return searchableColumns.some(column => {
          const value = item[column];
          if (value === null || value === undefined) {
            return false;
          }
          return value.toString().toLowerCase().includes(search);
        });
      });
    },
    // Stats presentation is canonical-only; unavailable membership stays neutral.
    statsStatusCode(): string {
      const stats = (this as any).talentStats as HygienistStats | null;
      if (!stats) return "";
      const ffsCode = stats.fairFreshStart?.status?.currentStatusCode;
      return resolveCanonicalFfsPresentationCode(ffsCode) || "";
    },
    // T2-6935: privileged Hold/Block actions require both a raw reason and a
    // durable evidence/case reference.
    canSubmitNotAFit(): boolean {
      return !!(
        ((this as any).modalNote || "").trim() &&
        ((this as any).modalTicketRef || "").trim()
      );
    }
  },
  methods: {
    ...mapMutations(["modify"]),
    async filterDays() {
      let data = await filterDays(this.countDays, this.tablename.toLowerCase());
      this.$store.commit("modify", {
        property: "tableContent",
        value: data
      });
      this.showModal = false;
    },
    updateCountDays(value) {
      this.$store.commit("modify", {
        property: "countDays",
        value: value
      });
    },
    async reviewed(item) {
      await (this.$refs.signupReviewDialog as any).open(item);
    },
    openModal(item, action, header) {
      // T2-6429: open the Not a Fit confirmation for Hold/Block. Both reset the
      // required note + ticket reference; modalAction preserves which was clicked.
      this.modalAction = action;
      this.modalNote = "";
      this.modalTicketRef = "";
      this.modalSubmitting = false;
      this.modalIdempotencyKey = createAdminSourceCorrectionIdempotencyKey(
        `member-review-${action}`
      );
      this.showModal = true;
      this.modalData = item;
      this.modalHeader = header;
    },
    // T2-6429: Hold and Block both resolve to the shared manual Not a Fit path
    // (T2-6396) instead of writing legacy Hold/Blocked status values. The clicked
    // action is prefixed into the support note so audit can tell Hold from Block.
    async submitNotAFit() {
      if (!(this as any).canSubmitNotAFit || this.modalSubmitting) {
        return;
      }
      const actionLabel = this.modalAction === "block" ? "Block" : "Hold";
      const note = `${actionLabel} (new-member review): ${this.modalNote.trim()}`;
      this.modalSubmitting = true;
      try {
        await applyManualNotAFit(this.modalData.id, {
          note,
          ticketReference: this.modalTicketRef.trim(),
          reasonCategory: getNewMemberReviewReasonCategory(actionLabel)
        }, this.modalIdempotencyKey);
        // Queue completion is part of the audited Not a Fit transaction.
        // Ownership warnings remain unresolved and the hold remains active.
        this.showModal = false;
        this.modalNote = "";
        this.modalTicketRef = "";
        this.modalAction = "";
        this.modalIdempotencyKey = "";
        await this.filterDays();
        this.$store.commit("callSnackbar", {
          visible: true,
          color: "success",
          text: `${actionLabel} applied — Talent set to Not a Fit.`
        });
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Could not apply Not a Fit.";
        this.$store.commit("callSnackbar", {
          visible: true,
          color: "error",
          text: message
        });
      } finally {
        this.modalSubmitting = false;
      }
    },
    // T2-6362/6374: read-only FFS status helpers for the table + stats dialog.
    rowStatusCode(item: any): string {
      return resolveCanonicalFfsPresentationCode(item?.currentStatusCode) || "";
    },
    getStatusLabel(code: string): string {
      return getFfsStatusLabel(code);
    },
    statusNeedsDarkText(code: string): boolean {
      return ffsStatusNeedsDarkText(code);
    },

    // Stats dialog methods
    async openStatsDialog(item: { id: number }) {
      this.selectedItemId = item.id;
      this.showStatsDialog = true;
      this.loadingStats = true;
      this.talentStats = null;

      try {
        const stats = await getHygienistStats(item.id);
        this.talentStats = stats;
      } catch (error) {
        console.error("Failed to fetch talent stats:", error);
        this.talentStats = null;
      } finally {
        this.loadingStats = false;
      }
    },

    getStatusColor(code: string): string {
      return getFfsStatusColor(code);
    },

    getStarColor(score: number | null): string {
      if (score === null) return "grey";
      if (score >= 4.5) return "#4CAF50"; // Green
      if (score >= 4.0) return "#8BC34A"; // Light green
      if (score >= 3.5) return "#FF9800"; // Orange
      return "#F44336"; // Red
    },

    getValueGivesColor(value: number | null): string {
      if (value === null) return "grey";
      if (value >= 0) return "#4CAF50"; // Green
      if (value >= -5) return "#FF9800"; // Orange
      return "#F44336"; // Red
    },

    getCancellationColor(percentage: number | null): string {
      if (percentage === null) return "grey";
      if (percentage <= 5) return "#4CAF50"; // Green
      if (percentage <= 10) return "#8BC34A"; // Light green
      if (percentage <= 15) return "#FF9800"; // Orange
      return "#F44336"; // Red
    }
  }
});
